import React, { useEffect, useCallback, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';

interface GoogleSignInProps {
  onSuccess: (credential: string) => void;
  onError: (error: string) => void;
  text?: string;
}

declare global {
  interface Window {
    google: any;
  }
}

const GoogleSignIn: React.FC<GoogleSignInProps> = ({ onSuccess, onError, text = "Continue with Google" }) => {
  const [isNative] = useState(Capacitor.isNativePlatform());

  // Handle native Google Sign-In via Capgo Social Login with retry logic
  const handleNativeSignIn = async (retryCount = 0): Promise<void> => {
    const MAX_RETRIES = 2;
    try {
      console.log(`[GoogleSignIn] Attempting native sign-in (attempt ${retryCount + 1})`);
      const result = await SocialLogin.login({ provider: 'google', options: {} });
      
      if (result.result.responseType === 'online' && result.result.idToken) {
        console.log('[GoogleSignIn] Successfully obtained ID token');
        onSuccess(result.result.idToken);
      } else {
        console.error('[GoogleSignIn] No ID token in response:', result);
        onError('Google sign-in failed: No ID token received');
      }
    } catch (error: any) {
      console.error(`[GoogleSignIn] Native sign-in error (attempt ${retryCount + 1}):`, error);
      
      const errorMsg = error.message || error.errorMessage || '';
      
      // Check if user canceled the sign-in flow
      const isCanceled = errorMsg.toLowerCase().includes('cancel') ||
                        errorMsg.toLowerCase().includes('user_cancelled') ||
                        errorMsg.toLowerCase().includes('user cancelled') ||
                        errorMsg.toLowerCase().includes('user canceled') ||
                        error.code === 'SIGN_IN_CANCELLED' ||
                        error.code === 'USER_CANCELLED';
      
      // If user canceled, silently return without showing error
      if (isCanceled) {
        console.log('[GoogleSignIn] User canceled sign-in flow');
        return;
      }
      
      // Check if it's a network/EOF error that might benefit from retry
      const isNetworkError = errorMsg.toLowerCase().includes('eof') || 
                            errorMsg.toLowerCase().includes('network') ||
                            errorMsg.toLowerCase().includes('timeout') ||
                            errorMsg.toLowerCase().includes('connection');
      
      if (isNetworkError && retryCount < MAX_RETRIES) {
        console.log(`[GoogleSignIn] Network error detected, retrying in 1 second...`);
        // Wait 1 second before retry to allow network to stabilize
        await new Promise(resolve => setTimeout(resolve, 1000));
        return handleNativeSignIn(retryCount + 1);
      }
      
      onError(errorMsg || 'Google sign-in failed');
    }
  };

  const handleCredentialResponse = useCallback((response: any) => {
    if (response.credential) {
      onSuccess(response.credential);
    } else {
      onError('Google sign-in failed');
    }
  }, [onSuccess, onError]);

  const initializeGoogleSignIn = useCallback(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    
    if (!clientId) {
      console.error('Google Client ID not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your .env file');
      onError('Google sign-in not configured');
      return;
    }

    if (window.google && clientId) {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredentialResponse,
      });

      window.google.accounts.id.renderButton(
        document.getElementById('google-signin-button'),
        {
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
        }
      );
    }
  }, [handleCredentialResponse, onError]);

  useEffect(() => {
    // Only load web Google Sign-In script on non-native platforms
    if (isNative) {
      return;
    }

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogleSignIn;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [initializeGoogleSignIn, isNative]);

  // Render native button for Capacitor or web button for browser
  if (isNative) {
    return (
      <div className="google-signin-container">
        <button
          onClick={() => handleNativeSignIn()}
          className="google-signin-button-native"
          type="button"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" style={{ marginRight: '8px' }}>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {text}
        </button>
      </div>
    );
  }

  return (
    <div className="google-signin-container">
      <div id="google-signin-button"></div>
    </div>
  );
};

export default GoogleSignIn;
